import clsx from "clsx";
import { type ReactNode, useEffect, useState } from "react";
import { IconContext } from "react-icons";
import { FaBell, FaChevronLeft } from "react-icons/fa";

import { Modal } from "@src/components/Modal";

import { useMount } from "@src/hooks/useMount";

import {
  type TNotification,
  initNotificationService,
} from "@src/services/notification";

import { formatDateStr } from "@src/utils/formatDateStr";

import "./style.less";
import type { NotificationProps } from "./types";

const isExpired = (n: TNotification) =>
  new Date(n.expires_at.replace(" ", "T")) < new Date();

export function Notification({ radio }: NotificationProps) {
  const [status, setStatus] = useState<
    "loading" | "connected" | "disconnected"
  >("loading");
  const [list, setList] = useState<TNotification[]>(() =>
    Array.isArray(radio.notifications)
      ? radio.notifications.filter((n) => !isExpired(n))
      : [],
  );
  const [notification, setNotification] = useState<TNotification | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  useMount(() => {
    const endNotificationService = initNotificationService(
      radio,
      (notification) => {
        // onNotification
        if (isExpired(notification)) return;

        // Adiciona a notificação na lista
        setList((prev) => [
          notification,
          ...prev.filter((_) => _.id !== notification.id),
        ]);

        // Exibe a notificação
        setNotification(notification);
      },
      () => setStatus("connected"), // onConnect
      () => setStatus("disconnected"), // onDisconnect
      () => setStatus("loading"), // onConnecting
    );
    return () => {
      endNotificationService();
    };
  });

  useEffect(() => {
    if (!notification) return;

    // Abre a modal ao chegar uma notificação
    if (!isOpen) setIsOpen(true);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notification]);

  useEffect(() => {
    if (!isOpen && notification) {
      setNotification(null);
    }
  }, [isOpen, notification]);

  useEffect(() => {
    const interval = setInterval(() => {
      setList((prev) => prev.filter((n) => !isExpired(n)));
      setNotification((current) =>
        current && isExpired(current) ? null : current,
      );
    }, 60_000);
    return () => clearInterval(interval);
  }, []);

  let renderList: ReactNode = null;
  if (list.length === 0) {
    renderList = <p>Nenhuma notificação por enquanto.</p>;
  } else {
    renderList = list.map((item) => (
      <div
        key={item.id}
        className="preview clickable"
        onClick={() => setNotification(item)}
      >
        <img alt={item.title} src={item?.image || "/logo_white.png"} />
        <div className="column">
          <div className="text">{item.title}</div>
          <div className="date">{formatDateStr(item.created_at)}</div>
        </div>
      </div>
    ));
  }

  return (
    <>
      <IconContext.Provider
        value={{ color: "#ffffff", className: "icon", size: "1.5em" }}
      >
        <div
          className="Notification-component clickable"
          onClick={() => setIsOpen(true)}
          data-tooltip-id="footer"
          data-tooltip-content="Notificações"
          data-tooltip-place="top"
        >
          <FaBell />
          <div className={clsx("bolinha", status)}>{list.length}</div>
        </div>
      </IconContext.Provider>
      <Modal
        className="Notification-modal"
        isOpen={isOpen}
        setIsOpen={setIsOpen}
      >
        {notification ? (
          <>
            <h2 className="clickable" onClick={() => setNotification(null)}>
              <FaChevronLeft /> {notification.title}
            </h2>
            <hr />
            <div className="container selected">
              {notification.image && (
                <img alt={notification.title} src={notification.image} />
              )}
              {notification.text && (
                <div
                  className="ck-content"
                  dangerouslySetInnerHTML={{ __html: notification.text }}
                />
              )}
              <div className="date">
                {formatDateStr(notification.created_at)}
              </div>
            </div>
          </>
        ) : (
          <>
            <h2>Notificações</h2>
            <hr />
            <div className="container">
              <div className="list">{renderList}</div>
            </div>
          </>
        )}
      </Modal>
    </>
  );
}
